import React, { useState, useEffect, useRef } from "react";
import { LanguageProvider, useLanguage, t, getLangLabel } from "./lib/i18n";
import { Analytics } from "@vercel/analytics/react";

// ─── Constants ────────────────────────────────────────────────────────────────
import { SK, CSS, SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY, TOUR_STEPS } from "./constants";

// ─── Supabase & DB helpers ────────────────────────────────────────────────────
import { supabase, loadDB, syncDB, deleteFromDB, upsertOne } from "./lib/supabase";

// ─── Utilities ────────────────────────────────────────────────────────────────
import { setAiEnabled, aiProxy, fileToBase64, aiSearch, timeAgo, thcEmailHtml, notifEmailHtml, sendEmailNotif, capabilitiesHtml, compressImage, cropPhotoFromId, groupCatalogEntries, buildCatalogDisplay, isOfferLive, liveOffers, acceptedOffers } from "./lib/utils";

// ─── Components ───────────────────────────────────────────────────────────────
import { Logo, WatchSketch, AccessDenied, MaintenanceScreen, ErrorBoundary, WatchCard, SellerCard, SearchView } from "./components/common";
import { NotifBell } from "./components/NotifBell";
import { WelcomeModal, TourModal } from "./components/WelcomeModal";
import { AuthModal } from "./components/AuthModal";
import { UPIModal } from "./components/UPIModal";
import { ProfileEditModal } from "./components/ProfileEditModal";
import { MakeOfferModal, WithdrawOfferModal, AddressConfirmModal, EditOfferPriceModal } from "./components/MakeOfferModal";
import { RatingModal } from "./components/RatingModal";
import { DisputeModal } from "./components/DisputeModal";
import { ProfileGateModal } from "./components/ProfileGateModal";
import { AdminCatalogEdit } from "./components/AdminCatalogEdit";
import { BetaApprovePanel } from "./components/BetaApprovePanel";
import { MaintenanceToggle, AiDashboard } from "./components/MaintenanceToggle";

// ─── Views ────────────────────────────────────────────────────────────────────
import { EntryScreen } from "./views/EntryScreen";
import { HomeView } from "./views/HomeView";
import { RequestView } from "./views/RequestView";
import { AuthSvcView } from "./views/AuthSvcView";
import { MarketView } from "./views/MarketView";
import { SellerDash } from "./views/SellerDash";
import { CollectorDash, AddToCatalogView, RequestCollectorView } from "./views/CollectorDash";
import { CatalogBrowseView } from "./views/CatalogBrowseView";
import { UniverseView } from "./views/UniverseView";
import { WatchLabsView } from "./views/WatchLabsView";
import { WotdView } from "./views/WotdView";
import { StoriesView } from "./views/StoriesView";
import { ServicesView } from "./views/ServicesView";
import { ChatView } from "./views/ChatView";
import { AdminView } from "./views/AdminView";
import { BG_IMAGES } from "./bgManifest";

// ─── Background slideshow ─────────────────────────────────────────────────────
function BgSlideshow({ catalogEntries }) {
  const [imgs, setImgs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const idxRef = useRef(0); // tracks current idx without stale closure in interval

  useEffect(() => {
    if (BG_IMAGES.length) { setImgs(BG_IMAGES); return; }
    const urls = [];
    for (const e of (catalogEntries || [])) {
      if (e.status === "approved" && Array.isArray(e.photos)) {
        for (const p of e.photos) {
          if (typeof p === "string" && p.startsWith("http")) { urls.push(p); break; }
        }
      }
      if (urls.length >= 16) break;
    }
    if (urls.length) setImgs(urls);
  }, [catalogEntries]);

  // Preload current image before showing
  useEffect(() => {
    if (!imgs.length) return;
    setLoaded(false);
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.src = imgs[idx];
    return () => { img.onload = null; };
  }, [idx, imgs]);

  // Auto-advance using ref so interval deps stay stable
  useEffect(() => {
    if (imgs.length < 2) return;
    const iv = setInterval(() => {
      const next = (idxRef.current + 1) % imgs.length;
      idxRef.current = next;
      setIdx(next);
    }, 5500);
    return () => clearInterval(iv);
  }, [imgs]);

  const rightIdx = imgs.length > 1 ? (idx + Math.floor(imgs.length / 2)) % imgs.length : idx;
  if (!imgs.length || !loaded) return null;
  return <>
    <div className="thc-bg-slide thc-bg-slide-left"  style={{backgroundImage:`url(${imgs[idx]})`}}/>
    <div className="thc-bg-slide thc-bg-slide-right" style={{backgroundImage:`url(${imgs[rightIdx]})`}}/>
  </>;
}

// ─── COMING SOON SCREEN ───────────────────────────────────────────────────────
function ComingSoon({ name, desc, setView }) {
  return (
    <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 24px"}}>
      <div style={{textAlign:"center",maxWidth:440}}>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.25em",color:"var(--gold)",marginBottom:20,textTransform:"uppercase"}}>Coming Soon</div>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,marginBottom:14,lineHeight:1.15}}>{name}</div>
        <p style={{fontSize:14,color:"var(--ink2)",lineHeight:1.75,marginBottom:32}}>{desc}</p>
        <div style={{display:"inline-flex",gap:16,flexWrap:"wrap",justifyContent:"center"}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"var(--gold)",animation:"pulse 1.5s ease-in-out infinite"}}/>
          <div style={{width:8,height:8,borderRadius:"50%",background:"var(--gold)",animation:"pulse 1.5s ease-in-out infinite",animationDelay:"0.3s"}}/>
          <div style={{width:8,height:8,borderRadius:"50%",background:"var(--gold)",animation:"pulse 1.5s ease-in-out infinite",animationDelay:"0.6s"}}/>
        </div>
        <div style={{marginTop:32}}>
          <button className="btn-ghost" style={{fontSize:13}} onClick={()=>setView("home")}>← Back to home</button>
        </div>
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
function AppInner() {
  const { lang, openPicker: openLangPicker } = useLanguage();
  const [betaUnlocked, setBeta]  = useState(()=>{ try{return localStorage.getItem("thc-beta")==="1";}catch{return false;} });
  const [betaTimeLeft, setBetaTimeLeft] = useState(null); // seconds remaining
  const [maintenance,  setMaintenance] = useState(null); // {on, message}
  const [maintenanceReady, setMaintenanceReady] = useState(false);
  const [view, setView]         = useState("home");
  const [marketTab, setMarketTab] = useState("browse");
  const [adminTab, setAdminTab]   = useState("requests");
  const [initialChatRoom, setInitialChatRoom] = useState(null);
  const VIEW_THEMES = {
    // nav = accent/nav pill color  |  rgb = for glow  |  bg = page bg  |  dark = invert ink  |  bgImg = vignette photo
    home:          { nav:"#8B6400", rgb:"139,100,0",   bg:"#FAF8F4", dark:false, bgImg:null },
    catalog:       { nav:"#1A5C3A", rgb:"26,92,58",    bg:"#F0F7F2", dark:false, bgImg:"https://images.unsplash.com/photo-1524229321985-1e1989075d9b?w=1600&q=60&auto=format&fit=crop" },
    request:       { nav:"#1C3A6E", rgb:"28,58,110",   bg:"#EDF2FA", dark:false, bgImg:"https://images.unsplash.com/photo-1526648856597-c2b6745ad7bd?w=1600&q=60&auto=format&fit=crop" },
    track:         { nav:"#1C3A6E", rgb:"28,58,110",   bg:"#EDF2FA", dark:false, bgImg:"https://images.unsplash.com/photo-1633451238042-85d93d267866?w=1600&q=60&auto=format&fit=crop" },
    market:        { nav:"#8B3A00", rgb:"139,58,0",    bg:"#FDF5EC", dark:false, bgImg:"https://images.unsplash.com/photo-1772223712047-54d6a5efce33?w=1600&q=60&auto=format&fit=crop" },
    chat:          { nav:"#0F4A5C", rgb:"15,74,92",    bg:"#EEF8F9", dark:false, bgImg:"https://images.unsplash.com/photo-1578999935853-4ec5fa6c1f60?w=1600&q=60&auto=format&fit=crop" },
    services:      { nav:"#6B3510", rgb:"107,53,16",   bg:"#FDF6F0", dark:false, bgImg:"https://images.unsplash.com/photo-1567093322102-6bdd32fba67d?w=1600&q=60&auto=format&fit=crop" },
    "auth-svc":    { nav:"#2D3A8C", rgb:"45,58,140",   bg:"#EEEFFE", dark:false, bgImg:"https://images.unsplash.com/photo-1633451238208-11c8e6c1fed4?w=1600&q=60&auto=format&fit=crop" },
    wotd:          { nav:"#8C1F48", rgb:"140,31,72",   bg:"#FDEEF4", dark:false, bgImg:"https://images.unsplash.com/photo-1757396150871-7e3ac8eff2d4?w=1600&q=60&auto=format&fit=crop" },
    stories:       { nav:"#7A4200", rgb:"122,66,0",    bg:"#FDF4E3", dark:false, bgImg:"https://images.unsplash.com/photo-1444988510113-d8e9c6741202?w=1600&q=60&auto=format&fit=crop" },
    labs:          { nav:"#00C8FF", rgb:"0,200,255",   bg:"#080C14", dark:true,  bgImg:"https://images.unsplash.com/photo-1583198432859-635beb4e8600?w=1600&q=60&auto=format&fit=crop" },
    universe:      { nav:"#6366F1", rgb:"99,102,241",  bg:"#06090F", dark:true,  bgImg:null },
    "seller-dash": { nav:"#1A5C3A", rgb:"26,92,58",    bg:"#EEF6EE", dark:false, bgImg:"https://images.unsplash.com/photo-1524229321985-1e1989075d9b?w=1600&q=60&auto=format&fit=crop" },
    admin:         { nav:"#7F1D1D", rgb:"127,29,29",   bg:"#F9EEEE", dark:false, bgImg:"https://images.unsplash.com/photo-1772223712047-54d6a5efce33?w=1600&q=60&auto=format&fit=crop" },
  };
  const defaultTheme = { nav:"#111118", rgb:"17,17,24", bg:"#FAFAFA", dark:false, bgImg:null };

  const PAGE_TITLES = {
    home:       "THC.watch — Vintage Indian Watches · HMT · Allwyn · Ricoh",
    catalog:    "Catalog — Browse HMT, Allwyn & Ricoh Watches | THC.watch",
    request:    "Find a Vintage Watch — From ₹99 | THC.watch",
    "auth-svc": "Authenticate a Watch — Certificate Issued | THC.watch",
    market:     "Buy / Sell — Vintage Watch Marketplace | THC.watch",
    track:      "Track Your Order | THC.watch",
  };
  useEffect(() => { document.title = PAGE_TITLES[view] || PAGE_TITLES.home; }, [view]);
  // Auto-clear chat notifications when user opens the Chat view
  useEffect(() => { if (view === "chat" && session) markChatNotifsRead(); }, [view]); // eslint-disable-line
  // Inject per-view theme as CSS variables + body class for dark pages
  useEffect(() => {
    const t = VIEW_THEMES[view] || defaultTheme;
    document.documentElement.style.setProperty("--accent", t.nav);
    document.documentElement.style.setProperty("--grn",    t.nav);
    document.documentElement.style.setProperty("--bg",     t.bg);
    if (t.dark) {
      document.documentElement.style.setProperty("--ink",  "#E2E8F0");
      document.documentElement.style.setProperty("--ink2", "#94A3B8");
      document.documentElement.style.setProperty("--ink3", "#4A5568");
      document.documentElement.style.setProperty("--bd",   "#1E293B");
      document.documentElement.style.setProperty("--bd2",  "#152238");
      document.documentElement.style.setProperty("--bg2",  "#0D1525");
      document.documentElement.style.setProperty("--bg3",  "#141C2E");
      document.body.classList.add("page-dark");
    } else {
      document.documentElement.style.setProperty("--ink",  "#111118");
      document.documentElement.style.setProperty("--ink2", "#3F3F46");
      document.documentElement.style.setProperty("--ink3", "#71717A");
      document.documentElement.style.setProperty("--bd",   "#E4E4E7");
      document.documentElement.style.setProperty("--bd2",  "#F0F0F2");
      document.documentElement.style.setProperty("--bg2",  "#F4F4F5");
      document.documentElement.style.setProperty("--bg3",  "#E4E4E7");
      document.body.classList.remove("page-dark");
    }
    if (view === "labs") document.body.classList.add("labs-mode");
    else document.body.classList.remove("labs-mode");
  }, [view]); // eslint-disable-line

  // Beta expiry countdown — 3 days from first unlock, exempt for admins
  useEffect(() => {
    const at = parseInt(localStorage.getItem("thc-beta-at") || "0", 10);
    if (!at) return;
    const EXPIRY_MS = 3 * 24 * 60 * 60 * 1000;
    const tick = () => {
      const left = Math.floor((at + EXPIRY_MS - Date.now()) / 1000);
      if (left <= 0) {
        try { localStorage.removeItem("thc-beta"); localStorage.removeItem("thc-beta-at"); } catch {}
        setBeta(false);
        setBetaTimeLeft(0);
      } else {
        setBetaTimeLeft(left);
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [betaUnlocked]);
  const [inventory, setInv]     = useState([]);
  const [requests, setReqs]     = useState([]);
  const [sellers, setSellers]   = useState([]);
  const [listings, setListings] = useState([]);
  const [users, setUsers]           = useState([]);
  const [collectorRequests, setCRs]   = useState([]);
  const [betaRequests, setBetaReqs]   = useState([]);
  const [watchModels,  setWatchModels] = useState([]);
  const [reviews,      setReviews]     = useState([]);
  const [creditTxns,   setCreditTxns]  = useState([]);
  const [watchDesigns, setWatchDesigns]= useState([]);
  const [designOrders, setDesignOrders]= useState([]);
  const [aiEnabled, setAiEnabledState] = useState(true);
  const [tickerEnabled, setTickerEnabled] = useState(true);
  const [aiBannerEnabled, setAiBannerEnabled] = useState(true);
  const [catalogEntries, setCatalog]= useState([]);
  const [notifs, setNotifs]         = useState([]);
  const [session, setSession]       = useState(null);
  const [showAuth, setShowAuth]       = useState(()=>{ try { return new URLSearchParams(window.location.search).get("join")==="1"; } catch { return false; } });
  const [inviteParams, setInviteParams] = useState(null);
  // Resolve opaque invite token from Supabase on mount
  useEffect(()=>{
    try {
      const token = new URLSearchParams(window.location.search).get("invite");
      if (!token || !token.startsWith("inv_")) return;
      supabase.from("app_settings").select("value").eq("id", token).maybeSingle().then(({data})=>{
        if (data?.value?.adminInvited) { setInviteParams(data.value); setShowAuth(true); }
      });
    } catch {}
  // eslint-disable-next-line
  }, []);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour,    setShowTour]    = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [toast, setToast]             = useState(null);
  const [liveNotif, setLiveNotif]     = useState(null);  // real-time pop-up
  const [sellerDashTab, setSellerDashTab] = useState("overview");
  const [showProfileGate, setShowProfileGate] = useState(false);
  const [profileGateCb,   setProfileGateCb]   = useState(null);
  const [navStates, setNavStates] = useState({}); // key → "hidden"|"coming_soon"|"new"|null
  const [mounted, setMounted]         = useState(false);

  useEffect(()=>{
    // Restore Supabase Auth JWT session (for RLS) — runs silently alongside custom session
    supabase.auth.getSession().catch(()=>{});
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(async (event, authSession) => {
      // Handle Google / social OAuth sign-in
      if (event === "SIGNED_IN" && (authSession?.user?.app_metadata?.providers || []).includes("google")) {
        const authUser = authSession.user;
        const email = authUser.email;
        // Defer DB calls — Supabase holds an internal lock during token exchange;
        // querying inside onAuthStateChange causes both to deadlock.
        setTimeout(async () => {
          try {
            if (!authUser.user_metadata?.email_verified) {
              console.warn("OAuth: email not verified by Google, blocking login");
              return;
            }
            const { data: existing } = await supabase.from("users").select("*").eq("email", email).maybeSingle();
            if (existing) {
              if (!existing.auth_id) {
                supabase.from("users").update({ auth_id: authUser.id }).eq("id", existing.id).then(() => {}).catch(() => {});
              }
              const u = { ...existing, auth_id: existing.auth_id || authUser.id };
              const ses = { user: u, loginAt: new Date().toISOString() };
              setSession(ses); persist(SK.session, ses); setShowAuth(false);
            } else {
              const uid = "u" + Date.now();
              const newUser = { id: uid, name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || "", email, role: "buyer", roles: ["buyer"], auth_id: authUser.id, createdAt: new Date().toISOString() };
              const { error: insErr } = await supabase.from("users").insert(newUser);
              if (!insErr) {
                setUsers(prev => [...prev, newUser]);
                const ses = { user: newUser, loginAt: new Date().toISOString() };
                setSession(ses); persist(SK.session, ses); setShowAuth(false); setShowWelcome(true);
              }
            }
          } catch(e) { console.warn("OAuth user sync error:", e.message); }
        }, 0);
      }
    });
    // Restore custom session synchronously before any async work
    try { const s = localStorage.getItem(SK.session); if (s) setSession(JSON.parse(s)); } catch {}

    (async()=>{
      // On OAuth redirect Supabase holds an internal auth lock while processing
      // the #access_token hash — any DB query blocks behind it indefinitely.
      // Mount the app immediately so the user isn't stuck, then load data after.
      const isOAuth = window.location.hash.includes("access_token");
      if (isOAuth) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setMounted(true);
        setMaintenanceReady(true); // wave 2 corrects this if maintenance is actually on
      }

      // Wave 1 — data needed for first render (all in parallel)
      try {
        const [inv, reqs, sels, lsts, cat, navStateRes] = await Promise.all([
          loadDB(SK.inventory),
          loadDB(SK.requests),
          loadDB(SK.sellers),
          loadDB(SK.listings),
          loadDB(SK.catalogEntries),
          Promise.resolve(supabase.from("app_settings").select("value").eq("id","coming_soon_views").maybeSingle()).catch(()=>({})),
        ]);
        setInv(inv); setReqs(reqs); setSellers(sels); setListings(lsts); setCatalog(cat);
        const nsVal = navStateRes?.data?.value;
        if (nsVal && typeof nsVal === "object" && !Array.isArray(nsVal)) setNavStates(nsVal);
      } catch(e) { console.warn("Wave 1 load error:", e.message); }
      if (!isOAuth) setMounted(true);

      // Wave 2 — secondary data (parallel, doesn't block render)
      // Each item catches individually so one bad table never kills the rest.
      const safe = (p) => Promise.resolve(p).catch(() => []);
      const safeOne = (p) => Promise.resolve(p).catch(() => ({}));
      const [crs, betas, wms, revs, wds, dos, nots, usersRes, crTxns, aiRes, mtRes, tickerRes, aiBannerRes] = await Promise.all([
        safe(loadDB(SK.collectorRequests)),
        safe(loadDB(SK.betaRequests)),
        safe(loadDB(SK.watchModels)),
        safe(loadDB(SK.reviews)),
        safe(loadDB(SK.watchDesigns)),
        safe(loadDB(SK.designOrders)),
        safe(loadDB(SK.notifications)),
        safeOne(supabase.from(SK.users).select("id,name,email,role,roles,phone,sellerId,createdAt,status,suspended,bio,phoneVerified,collectorId,avatar,restrictedActions,buyerRating,buyerFlags,addressLine1,addressLine2,addressCity,addressState,addressPostalCode,addressCountry,credits")),
        safe(loadDB(SK.creditTxns)),
        safeOne(supabase.from("app_settings").select("value").eq("id","ai_enabled").maybeSingle()),
        safeOne(supabase.from("app_settings").select("value").eq("id","maintenance").maybeSingle()),
        safeOne(supabase.from("app_settings").select("value").eq("id","ticker_enabled").maybeSingle()),
        safeOne(supabase.from("app_settings").select("value").eq("id","ai_banner_enabled").maybeSingle()),
      ]);
      setCRs(crs); setBetaReqs(betas); setWatchModels(wms); setReviews(revs);
      setWatchDesigns(wds); setDesignOrders(dos); setNotifs(nots);
      setUsers(usersRes.data || []); setCreditTxns(crTxns);
      const aiVal = aiRes.data?.value;
      const isAi = aiVal !== false && aiVal !== "false";
      setAiEnabled(isAi); setAiEnabledState(isAi);
      const mtVal = mtRes.data?.value;
      if (mtVal && typeof mtVal === "object") setMaintenance(mtVal);
      else if (mtVal === true) setMaintenance({ on:true, message:"We're making improvements. Back shortly." });
      setMaintenanceReady(true);
      const tkVal = tickerRes.data?.value;
      if (tkVal === false || tkVal === "false") setTickerEnabled(false);
      const abVal = aiBannerRes.data?.value;
      if (abVal === false || abVal === "false") setAiBannerEnabled(false);
    })();
    return () => { authSub.unsubscribe(); };
  },[]);

  const playNotifSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
    } catch {}
  };

  // Realtime subscription for notifications — reconnects whenever user logs in/out
  useEffect(() => {
    if (!session?.user?.id) return;
    const uid = session.user.id;
    const isAdminUser = (session.user.roles||[]).includes("admin") || session.user.role === "admin";
    const ch = supabase.channel("notifs:" + uid)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"notifications" }, payload => {
        const n = payload.new;
        const mine = isAdminUser ? (n.userId==="admin" || n.userId===uid) : n.userId===uid;
        if (mine) {
          setNotifs(prev => [n, ...prev]);
          playNotifSound();
          setLiveNotif(n);
          setTimeout(() => setLiveNotif(ln => ln?.id===n.id ? null : ln), 5000);
        }
      })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"notifications" }, payload => {
        setNotifs(prev => prev.map(x => x.id===payload.new.id ? payload.new : x));
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [session?.user?.id]);

  const persist = async (k, v) => {
    if (k === SK.session) {
      try { v ? localStorage.setItem(k, JSON.stringify(v)) : localStorage.removeItem(k); } catch {}
      return;
    }
    await syncDB(k, v);
  };
  const saveInv     = v => { setInv(v);      persist(SK.inventory,v); };
  const saveReqs    = v => { setReqs(v);     persist(SK.requests,v); };
  const refreshReqs = async () => { const d = await loadDB(SK.requests); setReqs(d); };
  // sellers: state-only — each mutation site does its own targeted DB write
  // to prevent bulk upserts from stale sessions overwriting admin approval flags.
  const saveSel     = v => { setSellers(v); };
  const saveLst     = v => { setListings(v); persist(SK.listings,v); };
  const saveCRs     = v => { setCRs(v);      persist(SK.collectorRequests,v); };
  const saveBetaReqs = v => { setBetaReqs(v); persist(SK.betaRequests,v); };
  const saveWMs      = v => { setWatchModels(v); persist(SK.watchModels,v); };
  const saveReviews  = v => { setReviews(v);     persist(SK.reviews,v); };
  const saveDesigns      = v => { setWatchDesigns(v); persist(SK.watchDesigns,v); };
  const saveDesignOrders = v => { setDesignOrders(v); persist(SK.designOrders,v); };
  const saveAdminSetting = (key, value) =>
    supabase.functions.invoke("admin-settings", { body: { userId: session?.user?.id, key, value } });

  const toggleAi = async (val) => {
    setAiEnabled(val); setAiEnabledState(val);
    await saveAdminSetting("ai_enabled", val);
  };
  const toggleMaintenance = async (on, message="We're making improvements. Back shortly.") => {
    const val = on ? { on:true, message } : { on:false, message };
    setMaintenance(val);
    await saveAdminSetting("maintenance", val);
  };
  const toggleTicker = async (val) => {
    setTickerEnabled(val);
    await saveAdminSetting("ticker_enabled", val);
  };
  const toggleAiBanner = async (val) => {
    setAiBannerEnabled(val);
    await saveAdminSetting("ai_banner_enabled", val);
  };
  const setNavState = async (viewKey, state) => {
    const next = { ...navStates };
    if (!state) delete next[viewKey]; else next[viewKey] = state;
    setNavStates(next);
    await saveAdminSetting("coming_soon_views", next);
  };
  const saveCatalog = v => { setCatalog(v);  persist(SK.catalogEntries,v); };
  const showToast = (msg,dur=3000) => { setToast(msg); setTimeout(()=>setToast(null),dur); };

  const _updateUserCredits = (userId, newBal) => {
    setUsers(prev => prev.map(u => u.id === userId ? {...u, credits: newBal} : u));
    if (session?.user?.id === userId) setSession(prev => ({...prev, user: {...prev.user, credits: newBal}}));
  };
  const addCredit = async (userId, amount, requestId, note) => {
    const u = users.find(x => x.id === userId);
    if (!u) return;
    const newBal = (u.credits||0) + amount;
    const txn = { id:`CTX-${Date.now().toString(36).toUpperCase()}`, user_id:userId, amount, type:"earned", request_id:requestId||null, note:note||null, created_at:new Date().toISOString() };
    await Promise.all([
      supabase.from(SK.users).update({ credits: newBal }).eq("id", userId),
      supabase.from(SK.creditTxns).insert(txn),
    ]);
    setCreditTxns(prev => [txn, ...prev]);
    _updateUserCredits(userId, newBal);
  };
  const deductCredit = async (userId, amount, requestId, note) => {
    const u = users.find(x => x.id === userId);
    if (!u || (u.credits||0) < amount) return null;
    const newBal = (u.credits||0) - amount;
    const txnId = `CREDIT-${Date.now().toString(36).toUpperCase()}`;
    const txn = { id:txnId, user_id:userId, amount:-amount, type:"spent", request_id:requestId||null, note:note||null, created_at:new Date().toISOString() };
    await Promise.all([
      supabase.from(SK.users).update({ credits: newBal }).eq("id", userId),
      supabase.from(SK.creditTxns).insert(txn),
    ]);
    setCreditTxns(prev => [txn, ...prev]);
    _updateUserCredits(userId, newBal);
    return txnId;
  };

  const addNotif = (userId, {type, title, body, link=null, emailTo=null, emailSubject=null, emailHtml=null, whatsappTo=null, whatsappTemplate=null, whatsappParams=null, whatsappWatch=null, whatsappRef=null, whatsappName=null}) => {
    const n = {id:"n"+Date.now()+Math.random().toString(36).slice(2,5), userId, type, title, body, link, read:false, createdAt:new Date().toISOString()};
    setNotifs(prev => { const upd=[...prev,n]; syncDB(SK.notifications,upd); return upd; });
    if (emailTo) sendEmailNotif(emailTo, emailSubject||title, emailHtml||notifEmailHtml(title,body));
    if (whatsappTo) {
      const waPayload = (whatsappTemplate && whatsappParams)
        ? { phone:whatsappTo, templateName:whatsappTemplate, params:whatsappParams }
        : { phone:whatsappTo, sellerName:whatsappName, watchName:whatsappWatch, requestRef:whatsappRef };
      fetch(`${SUPABASE_FUNCTIONS_URL}/send-whatsapp`, {
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":SUPABASE_ANON_KEY},
        body:JSON.stringify(waPayload),
      }).catch(()=>{});
    }
  };
  const markNotifRead = (id) => setNotifs(prev=>{ const upd=prev.map(n=>n.id===id?{...n,read:true}:n); syncDB(SK.notifications,upd); return upd; });
  const markAllNotifsRead = () => {
    const uid=session?.user?.id; const admin=session?.user?.role==="admin";
    setNotifs(prev=>{ const upd=prev.map(n=>(admin?(n.userId==="admin"||n.userId===uid):n.userId===uid)?{...n,read:true}:n); syncDB(SK.notifications,upd); return upd; });
  };
  const markChatNotifsRead = () => {
    setNotifs(prev=>{ const upd=prev.map(n=>n.link?.view==="chat"?{...n,read:true}:n); syncDB(SK.notifications,upd); return upd; });
  };

  const login = (user, isNew=false) => {
    const ses = { user, loginAt:new Date().toISOString() };
    setSession(ses); persist(SK.session,ses);
    setShowAuth(false);
    showToast(`Welcome${isNew?"":" back"}, ${user.name.split(" ")[0]} ✓`);
    if (inviteParams?.adminInvited) { setMarketTab("apply"); setView("market"); return; }
    if (isNew) { setShowWelcome(true); }
    else if (user.role==="admin")  setView("admin");
    else if (user.role==="seller") setView("seller-dash");
  };
  const logout = () => { setSession(null); persist(SK.session,null); setView("home"); showToast("Signed out."); };

  const openAuth = () => setShowAuth(true);

  // requireProfile(cb) — call before any buyer transaction. If phone missing, gate it.
  const requireProfile = (cb) => {
    if (!session?.user) { openAuth(); return; }
    const phone = (session.user.phone || "").replace(/\D/g, "");
    if (phone.length >= 7) { cb(); return; }
    setProfileGateCb(() => cb);
    setShowProfileGate(true);
  };

  if (!mounted) return (
    <div style={{background:"#FAFAFA",height:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}><Logo size="lg"/></div>
  );

  // Block render until maintenance status is known — prevents flash of normal app
  const isAdminUser = session?.user?.role==="admin" || (session?.user?.roles||[]).includes("admin");
  if (!maintenanceReady && !isAdminUser) return <div style={{minHeight:"100vh",background:"#111118"}}/>;

  // Maintenance mode — admin always bypasses
  if (maintenance?.on && !isAdminUser) return (
    <MaintenanceScreen message={maintenance.message} openAuth={()=>setShowAuth(true)}>
      {showAuth && <AuthModal onClose={()=>setShowAuth(false)} onLogin={(user,isNew)=>{
        const isAdmin = user.role==="admin"||(user.roles||[]).includes("admin");
        if (!isAdmin) { showToast("Access restricted during maintenance."); return; }
        login(user, isNew);
      }} users={users} saveUsers={v=>{setUsers(v);persist(SK.users,v);}} toast={showToast} addNotif={addNotif}/>}
    </MaintenanceScreen>
  );

  // beta gate removed — open access

  const u = session?.user;
  const userRoles   = u ? [...new Set([...(u.roles||[]), u.role].filter(Boolean))] : [];
  const isAdmin      = userRoles.includes("admin");
  const isModerator  = userRoles.includes("moderator");
  const isSeller     = isAdmin || userRoles.includes("seller");
  const isBuyer      = isAdmin || userRoles.includes("buyer");
  const isCollector  = isAdmin || userRoles.includes("collector");

  const ns = (v) => navStates[v] || null; // nav state for a view key

  const theme = VIEW_THEMES[view] || defaultTheme;
  const pendingCR   = collectorRequests.find(r=>r.userId===u?.id && r.status==="pending");
  const currentUser = u ? (users.find(x => x.id === u.id) || u) : null;
  const userCredits = currentUser?.credits || 0;

  const NAV_ICONS = {
    catalog:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    track:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    request:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    "auth-svc": <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    market:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>,
    labs:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H15M10 3v6l-4 9a1 1 0 00.9 1.4h10.2a1 1 0 00.9-1.4l-4-9V3"/></svg>,
    services:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>,
    chat:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  };
  const NAV_LINKS = [
    ["catalog", t("nav_catalog", lang)],
    ["request",  t("nav_findtrack", lang)],
    ["market",  t("nav_market", lang)],
    ["chat",    t("nav_chat", lang)],
    ...(u ? [["wotd", t("nav_wotd", lang)]] : []),
    ["auth-svc", t("nav_auth", lang)],
    ["services", t("nav_services", lang)],
    ["stories",  t("nav_stories", lang)],
    ["labs",     t("nav_labs", lang)],
  ];

  const menuBtnStyle = {display:"block",width:"100%",textAlign:"left",padding:"11px 16px",background:"none",border:"none",fontSize:13,cursor:"pointer",color:"var(--ink)",fontFamily:"'DM Sans',sans-serif"};

  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      <style>{CSS}</style>
      <style>{`.thc-bnav-btn.active{color:${theme.nav} !important;}.thc-bnav-btn.active::before{background:${theme.nav} !important;}.thc-bnav-btn.active .thc-bnav-label{color:${theme.nav} !important;}`}</style>
      {/* Layer 1 — solid page background, transitions between views */}
      <div style={{position:"fixed",inset:0,background:`var(--bg)`,zIndex:-3,transition:"background .5s ease",pointerEvents:"none"}}/>
      {/* Layer 2 — per-view photo vignette: visible on both edges, fades toward center */}
      {theme.bgImg && (
        <div key={theme.bgImg} style={{
          position:"fixed", inset:0, zIndex:-2, pointerEvents:"none",
          backgroundImage:`url(${theme.bgImg})`,
          backgroundSize:"cover", backgroundPosition:"center 25%",
          opacity: theme.dark ? 0.5 : 0.38,
          filter:`blur(1px) saturate(${theme.dark ? 0.75 : 0.6})`,
          maskImage:"linear-gradient(to right, rgba(0,0,0,.9) 0%, rgba(0,0,0,.5) 18%, transparent 38%, transparent 62%, rgba(0,0,0,.5) 82%, rgba(0,0,0,.9) 100%)",
          WebkitMaskImage:"linear-gradient(to right, rgba(0,0,0,.9) 0%, rgba(0,0,0,.5) 18%, transparent 38%, transparent 62%, rgba(0,0,0,.5) 82%, rgba(0,0,0,.9) 100%)",
          transition:"opacity .7s ease",
        }}/>
      )}
      {/* Layer 3 — accent glow at top + subtle edge darkening */}
      <div style={{position:"fixed",inset:0,zIndex:-1,pointerEvents:"none",
        background:`radial-gradient(ellipse 65% 35% at 50% 0%, rgba(${theme.rgb},.11) 0%, transparent 60%),
                    radial-gradient(ellipse 100% 15% at 50% 100%, rgba(0,0,0,.05) 0%, transparent 70%)`,
        transition:"background .6s ease"}}/>
      {/* BgSlideshow: only on home where there's no specific vignette */}
      {view==="home" && <BgSlideshow catalogEntries={catalogEntries} />}

      {/* Global backdrops — rendered at root so backdrop-filter on header doesn't trap their z-index */}
      {showUserMenu && <div style={{position:"fixed",inset:0,zIndex:9998}} onClick={()=>setShowUserMenu(false)}/>}

      {toast &&<div style={{position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",background:"#111118",color:"#fff",padding:"12px 24px",borderRadius:50,zIndex:10001,fontFamily:"'DM Mono',monospace",fontSize:12,letterSpacing:"0.06em",whiteSpace:"nowrap",boxShadow:"0 8px 24px rgba(0,0,0,.2)"}}>{toast}</div>}

      {/* Real-time notification pop-up */}
      {liveNotif && (
        <div onClick={()=>{ setLiveNotif(null); if(liveNotif.link?.view){ if(liveNotif.link.view==="seller-dash"&&liveNotif.link.tab) setSellerDashTab(liveNotif.link.tab); if(liveNotif.link.view==="admin"&&liveNotif.link.tab) setAdminTab(liveNotif.link.tab); if(liveNotif.link.view==="market"&&liveNotif.link.tab) setMarketTab(liveNotif.link.tab); if(liveNotif.link.roomId) setInitialChatRoom(liveNotif.link.roomId); setView(liveNotif.link.view); } }}
          style={{position:"fixed",top:16,right:16,zIndex:10000,width:300,background:"#fff",border:"1.5px solid var(--bd)",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,.15)",padding:"14px 16px",cursor:liveNotif.link?"pointer":"default",animation:"slideInRight .25s ease-out"}}>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"var(--accent)",flexShrink:0,marginTop:5}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:3,lineHeight:1.35}}>{liveNotif.title}</div>
              <div style={{fontSize:12,color:"var(--ink3)",lineHeight:1.5}}>{liveNotif.body}</div>
              {liveNotif.link?.view && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--accent)",marginTop:5,letterSpacing:"0.06em"}}>TAP TO VIEW →</div>}
            </div>
            <button onClick={e=>{e.stopPropagation();setLiveNotif(null);}} style={{background:"none",border:"none",color:"var(--ink3)",fontSize:16,cursor:"pointer",padding:"0 2px",lineHeight:1,flexShrink:0}}>×</button>
          </div>
        </div>
      )}

      {/* Beta expiry countdown — bottom-right, hidden for admins */}
      {betaTimeLeft > 0 && !isAdmin && (() => {
        const d = Math.floor(betaTimeLeft / 86400);
        const h = Math.floor((betaTimeLeft % 86400) / 3600);
        const m = Math.floor((betaTimeLeft % 3600) / 60);
        const s = betaTimeLeft % 60;
        const pad = n => String(n).padStart(2,"0");
        const urgent = betaTimeLeft < 3600;
        return (
          <div style={{position:"fixed",bottom:24,right:20,zIndex:9000,background:urgent?"#7F1D1D":"#111118",border:"1px solid "+(urgent?"#DC2626":"#2A2A3A"),borderRadius:10,padding:"8px 14px",boxShadow:"0 4px 20px rgba(0,0,0,0.3)",userSelect:"none"}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:"0.2em",color:urgent?"#FCA5A5":"#4B4B5A",textTransform:"uppercase",marginBottom:4}}>Beta expires in</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:15,color:urgent?"#FCA5A5":"#E4E4E7",letterSpacing:"0.08em",fontWeight:700}}>
              {d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`}
            </div>
          </div>
        );
      })()}

      {showAuth && <AuthModal onLogin={login} onClose={()=>setShowAuth(false)} onUserCreated={u => { const updated = [...users, u]; setUsers(updated); persist(SK.users, updated); }}/>}
      {showProfileGate && session && <ProfileGateModal session={session} users={users} saveUsers={v=>{setUsers(v);persist(SK.users,v);}} onClose={()=>{setShowProfileGate(false);setProfileGateCb(null);}} onComplete={updatedUser=>{const s={...session,user:updatedUser};setSession(s);persist(SK.session,s);const updated=users.map(x=>x.id!==updatedUser.id?x:updatedUser);setUsers(updated);persist(SK.users,updated);setShowProfileGate(false);profileGateCb?.();setProfileGateCb(null);}}/>}
      {showEditProfile && <ProfileEditModal session={session} users={users} sellers={sellers} saveSellers={saveSel} catalogEntries={catalogEntries} saveCatalog={saveCatalog} onSave={u=>{const s={...session,user:u};setSession(s);persist(SK.session,s);const updated=users.map(x=>x.id!==u.id?x:u);setUsers(updated);persist(SK.users,updated);}} toast={showToast} onClose={()=>setShowEditProfile(false)}/>}
      {showWelcome && <WelcomeModal session={session} onClose={()=>setShowWelcome(false)} setView={setView} onTour={()=>{setShowWelcome(false);setShowTour(true);}}/>}
      {showTour && <TourModal steps={TOUR_STEPS} onClose={()=>setShowTour(false)} setView={setView}/>}

      {/* AI disabled banner */}
      {!aiEnabled && aiBannerEnabled && (
        <div style={{background:"#B45309",color:"#fff",padding:"6px 24px",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.1em",textAlign:"center"}}>
          ⚠ AI FEATURES DISABLED — photo detection, ID validation and smart search are using basic fallbacks
        </div>
      )}

      {/* Ticker */}
      {tickerEnabled && (
        <div style={{background:"#111118",padding:"7px 0",overflow:"hidden"}}>
          <div className="ticker" style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#6EE7B7",letterSpacing:"0.18em"}}>
            {"HMT · ALLWYN · RICOH · TRUSTED SELLERS · SOURCING ₹99 · AUTHENTICITY ₹300 · EST. 2026 · JAIPUR · ".repeat(8)}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="thc-hdr" style={{background:theme.dark?"rgba(8,12,20,.92)":"rgba(249,247,244,.96)",borderBottom:`1.5px solid ${theme.nav}${theme.dark?"28":"20"}`,padding:"12px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:9999,backdropFilter:"blur(12px)",transition:"background .5s ease, border-color .4s ease"}}>
        <Logo size="sm" onClick={()=>setView("home")}/>
        <nav style={{display:"flex",gap:2,alignItems:"center"}}>
          <div className="thc-nav-links">
            {NAV_LINKS.filter(([v])=> isAdmin || ns(v) !== "hidden").map(([v,label])=>{
              const badge = v==="chat" ? notifs.filter(n=>!n.read && n.link?.view==="chat").length : 0;
              const state = ns(v);
              const chip = isAdmin && state === "hidden" ? ["HIDDEN","#6B7280","#fff"]
                         : state === "coming_soon"       ? ["SOON",  isAdmin?"#7C3AED":"#A78BFA", "#fff"]
                         : state === "new"               ? ["NEW",   "#D97706","#fff"]
                         : null;
              return (
                <button key={v} className={`thc-nav-btn${view===v?" active":""}`}
                  style={{background:view===v?theme.nav:"transparent",color:view===v?"#fff":"var(--ink2)",transition:"background .3s ease,color .3s ease",opacity:state==="hidden"&&isAdmin?0.5:1,flexDirection:"column",alignItems:"center",gap:1,paddingBottom:chip?5:undefined}}
                  onClick={()=>setView(v)}>
                  <span style={{display:"flex",alignItems:"center",gap:4}}>
                    {NAV_ICONS[v]}{label}{badge>0&&<span className="thc-nav-badge">{badge>9?"9+":badge}</span>}
                  </span>
                  {chip && <span style={{fontFamily:"'DM Mono',monospace",fontSize:6,letterSpacing:"0.12em",background:chip[1],color:chip[2],borderRadius:3,padding:"1px 5px",lineHeight:1.6,display:"block"}}>{chip[0]}</span>}
                </button>
              );
            })}
            <div style={{width:1,height:18,background:"#C8BFB0",margin:"0 6px"}}/>
          </div>

          {/* Language toggle — opens picker modal */}
          <button onClick={openLangPicker} style={{fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.08em",padding:"4px 9px",borderRadius:20,border:`1px solid ${theme.nav}40`,background:"transparent",cursor:"pointer",color:"var(--ink3)",marginRight:2,flexShrink:0}}>
            {getLangLabel(lang)}
          </button>

          {!u ? (
            <div style={{display:"flex",gap:6}}>
              <button className="btn-ghost" style={{fontSize:12,padding:"7px 14px"}} onClick={()=>openAuth("buyer")}>{t("nav_signin",lang)}</button>
              <button className="btn-ink"   style={{fontSize:12,padding:"7px 14px"}} onClick={()=>{setMarketTab("sell");setView("market");}}>{lang==="hi" ? "घड़ी बेचें" : "List a Watch"}</button>
            </div>
          ) : (
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <NotifBell notifs={notifs} session={session} onMarkRead={markNotifRead} onMarkAllRead={markAllNotifsRead} setView={setView} setMarketTab={setMarketTab} setAdminTab={setAdminTab} setSellerDashTab={setSellerDashTab}/>
              {/* User pill — single dropdown for everything */}
              <div style={{position:"relative"}}>
                <button onClick={()=>setShowUserMenu(p=>!p)} style={{width:34,height:34,borderRadius:"50%",background:"var(--accent)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",fontFamily:"'DM Mono',monospace",letterSpacing:"0.04em",position:"relative",flexShrink:0,userSelect:"none"}}>
                  {u.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                </button>
                {showUserMenu && (
                  <>
                    <div style={{position:"absolute",top:"calc(100% + 8px)",right:0,background:"#fff",border:"1px solid var(--bd)",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,.12)",zIndex:9999,minWidth:210,overflow:"hidden"}}>
                      <div style={{padding:"14px 16px",borderBottom:"1px solid var(--bd)"}}>
                        <div style={{fontSize:14,fontWeight:600}}>{u.name}</div>
                        <div style={{fontSize:11,color:"var(--ink3)",fontFamily:"'DM Mono',monospace",marginTop:3}}>{userRoles.join(" · ")}</div>
                      </div>
                      {isAdmin      && <button onClick={()=>{setView("admin");      setShowUserMenu(false);}} style={menuBtnStyle}>Admin Panel →</button>}
                      {isModerator && !isAdmin && <button onClick={()=>{setView("admin");setShowUserMenu(false);}} style={menuBtnStyle}>Moderator Panel →</button>}
                      {isSeller    && <button onClick={()=>{setView("seller-dash");setShowUserMenu(false);}} style={menuBtnStyle}>Seller Dashboard →</button>}
                      {isCollector && <button onClick={()=>{setView("add-catalog");setShowUserMenu(false);}} style={menuBtnStyle}>+ Add to Catalog →</button>}
                      {!isSeller && !isAdmin && <button onClick={()=>{setMarketTab("apply");setView("market");setShowUserMenu(false);}} style={menuBtnStyle}>Become a Seller →</button>}
                      {!isCollector && !isAdmin && !isSeller && <button onClick={()=>{setView("add-catalog");setShowUserMenu(false);}} style={menuBtnStyle}>Become a Collector →</button>}
                      <div style={{height:1,background:"var(--bd)"}}/>
                      <button onClick={()=>{setShowEditProfile(true);setShowUserMenu(false);}} style={menuBtnStyle}>Edit Profile</button>
                      <button onClick={()=>{setShowWelcome(true);setShowUserMenu(false);}} style={menuBtnStyle}>What I can do ✦</button>
                      <button onClick={()=>{setShowTour(true);setShowUserMenu(false);}} style={menuBtnStyle}>Take a tour ↗</button>
                      <button onClick={logout} style={{...menuBtnStyle,color:"var(--red)"}}>Sign out</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      <main className="thc-main" style={{flex:1,maxWidth:1060,margin:"0 auto",padding:"52px 24px 100px",width:"100%"}}>
        {view==="home"        && <HomeView        setView={setView} sellers={sellers} session={session} openAuth={openAuth} onSellClick={()=>{setMarketTab("apply");setView("market");}} catalogEntries={catalogEntries}/>}
        {view==="catalog"     && <CatalogBrowseView catalogEntries={catalogEntries} saveCatalog={saveCatalog} requests={requests} saveRequests={saveReqs} session={session} openAuth={openAuth} setView={setView} toast={showToast} addNotif={addNotif} watchModels={watchModels} userCredits={userCredits} deductCredit={deductCredit} sellers={sellers} users={users} openDealRoom={(roomId)=>{setInitialChatRoom(roomId);setView("chat");}}/>}
        {view==="universe"    && (ns("universe")==="coming_soon"&&!isAdmin ? <ComingSoon name="Watch Universe"     desc="An interactive 3-D map of every watch in our catalog — explore brands, models and variants." setView={setView}/> : <UniverseView     catalogEntries={catalogEntries} watchModels={watchModels} users={users} sellers={sellers} setView={setView} session={session} isAdmin={isAdmin}/>)}
        {view==="search"      && <SearchView        inventory={inventory}/>}
        {(view==="request"||view==="track") && <RequestView requests={requests} saveRequests={saveReqs} catalogEntries={catalogEntries} saveCatalog={saveCatalog} session={session} toast={showToast} addNotif={addNotif} sellers={sellers} saveSellers={saveSel} reviews={reviews} saveReviews={saveReviews} userCredits={userCredits} deductCredit={deductCredit} users={users} openAuth={openAuth} requireProfile={requireProfile} openDealRoom={(roomId)=>{setInitialChatRoom(roomId);setView("chat");}} setView={setView}/>}
        {view==="auth-svc"    && (ns("auth-svc")==="coming_soon"&&!isAdmin  ? <ComingSoon name="Authenticate a Watch" desc="Professional authenticity certificates issued by our horological experts — coming soon." setView={setView}/> : <AuthSvcView       requests={requests} saveRequests={saveReqs} session={session} openAuth={openAuth} toast={showToast} addNotif={addNotif} userCredits={userCredits} deductCredit={deductCredit}/>)}
        {view==="market"      && <MarketView        sellers={sellers} listings={listings} saveSellers={saveSel} saveListings={saveLst} requests={requests} saveRequests={saveReqs} users={users} saveUsers={v=>{setUsers(v);persist(SK.users,v);}} session={session} refreshSession={u=>{const s={...session,user:u};setSession(s);persist(SK.session,s);}} openAuth={openAuth} requireProfile={requireProfile} toast={showToast} setView={setView} initialTab={marketTab} onTabChange={setMarketTab} addNotif={addNotif} inviteParams={inviteParams}/>}
        {view==="chat"        && <ChatView          session={session} sellers={sellers} users={users} requests={requests} saveRequests={saveReqs} listings={listings} openAuth={openAuth} toast={showToast} catalogEntries={catalogEntries} saveCatalog={saveCatalog} watchModels={watchModels} setView={setView} addNotif={addNotif} notifs={notifs} initialRoom={initialChatRoom} onRoomOpened={()=>setInitialChatRoom(null)}/>}
        {view==="labs"        && (ns("labs")==="coming_soon"&&!isAdmin       ? <ComingSoon name="Watch Labs"          desc="AI-powered watch design studio — sketch, refine and visualise custom dials." setView={setView}/> : <WatchLabsView      session={session} openAuth={openAuth} toast={showToast} watchDesigns={watchDesigns} saveDesigns={saveDesigns} designOrders={designOrders} saveDesignOrders={saveDesignOrders} addNotif={addNotif} aiEnabled={aiEnabled}/>)}
        {view==="wotd"        && (ns("wotd")==="coming_soon"&&!isAdmin       ? <ComingSoon name="Watch of the Day"    desc="A daily community photo wall — post your HMT, Allwyn or Ricoh and join the conversation." setView={setView}/> : <WotdView           session={session} openAuth={openAuth} toast={showToast} aiEnabled={aiEnabled} watchModels={watchModels} saveWMs={saveWMs} catalogEntries={catalogEntries} saveCatalog={saveCatalog}/>)}
        {view==="stories"     && (ns("stories")==="coming_soon"&&!isAdmin    ? <ComingSoon name="HMT Stories"         desc="Community videos — personal histories, family heirlooms and restoration journeys." setView={setView}/> : <StoriesView        session={session} openAuth={openAuth}/>)}
        {view==="services"    && (ns("services")==="coming_soon"&&!isAdmin   ? <ComingSoon name="Services"            desc="Watch servicing, restoration and strap fitting by verified craftspeople." setView={setView}/> : <ServicesView       session={session} openAuth={openAuth} toast={showToast} requests={requests} saveRequests={saveReqs}/>)}
        {view==="add-catalog" && isCollector  && <CollectorDash catalogEntries={catalogEntries} saveCatalog={saveCatalog} requests={requests} session={session} toast={showToast}/>}
        {view==="add-catalog" && !isCollector && <RequestCollectorView collectorRequests={collectorRequests} saveCRs={saveCRs} session={session} openAuth={openAuth} pendingCR={pendingCR} toast={showToast}/>}
        {view==="seller-dash" && isSeller  && <SellerDash session={session} sellers={sellers} saveSellers={saveSel} listings={listings} saveListings={saveLst} requests={requests} saveRequests={saveReqs} refreshRequests={refreshReqs} catalogEntries={catalogEntries} saveCatalog={saveCatalog} toast={showToast} addNotif={addNotif} reviews={reviews} saveReviews={saveReviews} users={users} setView={setView} openDealRoom={(roomId)=>{setInitialChatRoom(roomId);setView("chat");}} initialTab={sellerDashTab}/>}
        {view==="admin"       && (isAdmin||isModerator) && <ErrorBoundary><AdminView  inventory={inventory} saveInventory={saveInv} requests={requests} saveRequests={saveReqs} sellers={sellers} saveSellers={saveSel} listings={listings} saveListings={saveLst} users={users} saveUsers={v=>{setUsers(v);persist(SK.users,v);}} collectorRequests={collectorRequests} saveCRs={saveCRs} catalogEntries={catalogEntries} saveCatalog={saveCatalog} betaRequests={betaRequests} saveBetaReqs={saveBetaReqs} watchModels={watchModels} saveWMs={saveWMs} watchDesigns={watchDesigns} saveDesigns={saveDesigns} designOrders={designOrders} saveDesignOrders={saveDesignOrders} aiEnabled={aiEnabled} toggleAi={toggleAi} maintenance={maintenance} toggleMaintenance={toggleMaintenance} tickerEnabled={tickerEnabled} toggleTicker={toggleTicker} aiBannerEnabled={aiBannerEnabled} toggleAiBanner={toggleAiBanner} navStates={navStates} setNavState={setNavState} session={session} toast={showToast} addNotif={addNotif} reviews={reviews} saveReviews={saveReviews} initialTab={adminTab} onTabChange={setAdminTab} addCredit={addCredit} isModerator={isModerator&&!isAdmin}/></ErrorBoundary>}
        {view==="admin"       && !(isAdmin||isModerator) && <AccessDenied openAuth={()=>openAuth("admin")}/>}
        {view==="seller-dash" && !isSeller && <AccessDenied openAuth={()=>openAuth("seller")}/>}
      </main>

      <footer className="thc-footer">
        <Logo size="sm" light/>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#D4A017",letterSpacing:"0.2em",marginBottom:4}}>HMT · ALLWYN · RICOH</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",letterSpacing:"0.1em"}}>@THEHOROLOGICALCATALOG · JAIPUR · 2026</div>
        </div>
      </footer>

      {/* Mobile bottom navigation */}
      <nav className="thc-bottom-nav" aria-label="Main navigation">
        <div className="thc-bnav-inner">
          {[
            { v:"catalog",  label:"Explore",   icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg> },
            { v:"request", label:"Find",      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> },
            { v:"market",  label:"Sell",      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14m-7-7h14"/></svg>, tab:"browse" },
            { v:"chat",    label:"Chat",      icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>, badge: notifs.filter(n=>!n.read && n.link?.view==="chat").length },
          ].map(({v,label,icon,badge,tab})=>(
            <button key={label} className={`thc-bnav-btn${view===v&&(!tab||marketTab===tab)?" active":""}`}
              style={view===v&&(!tab||marketTab===tab)?{color:theme.nav}:{}}
              onClick={()=>{ if(tab) setMarketTab(tab); setView(v); }}>
              <span style={{position:"relative",display:"inline-flex"}}>
                {icon}
                {badge>0 && <span style={{position:"absolute",top:-3,right:-5,width:14,height:14,borderRadius:"50%",background:"var(--red)",color:"#fff",fontSize:8,fontFamily:"'DM Mono',monospace",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",border:"1.5px solid #fff"}}>{badge>9?"9+":badge}</span>}
              </span>
              <span className="thc-bnav-label">{label}</span>
            </button>
          ))}
          {/* More button */}
          <button className="thc-bnav-btn" onClick={()=>setShowMoreSheet(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>
            <span className="thc-bnav-label">More</span>
          </button>
        </div>
      </nav>

      <Analytics/>
      {/* Mobile More sheet */}
      {showMoreSheet && (
        <>
          <div className="mob-sheet-backdrop" onClick={()=>setShowMoreSheet(false)}/>
          <div className="mob-sheet">
            <div className="mob-sheet-pill"/>
            <div className="mob-sheet-grid">
              {[
                { icon:"🏠", label:"Home",          desc:"Platform overview",              v:"home" },
                { icon:"◉", label:"Explore",        desc:"Browse & buy watches",           v:"catalog" },
                { icon:"📦", label:"My Orders",     desc:"Track your requests",            v:"track" },
                { icon:"🔍", label:"Find a Watch",  desc:"₹99 · We source it",            v:"request" },
                { icon:"🛡️", label:"Authenticate", desc:"₹300 · Certificate issued",      v:"auth-svc" },
                { icon:"🔧", label:"Services",      desc:"Servicing, straps & accessories",v:"services" },
                { icon:"⚗️", label:"Labs",          desc:"Custom watch design",            v:"labs" },
                { icon:"🌐", label:"Universe",      desc:"Explore watches visually",       v:"universe" },
                { icon:"📷", label:"Watch of the Day",desc:"Daily community wall",         v:"wotd" },
                { icon:"🎬", label:"HMT Stories",   desc:"Community video wall",           v:"stories" },
                ...(isAdmin ? [{ icon:"⚙️", label:"Admin Panel", desc:"Manage the platform", v:"admin" }] : []),
                ...(isModerator && !isAdmin ? [{ icon:"🛠️", label:"Moderator Panel", desc:"Approve sellers & catalog", v:"admin" }] : []),
                ...(isSeller && !isAdmin ? [{ icon:"🏪", label:"My Seller Dash", desc:"Listings & requests", v:"seller-dash" }] : []),
                ...(isCollector && !isAdmin ? [{ icon:"📚", label:"Add to Catalog", desc:"Share your watches", v:"add-catalog" }] : []),
                ...(!u ? [{ icon:"👋", label:"Sign in", desc:"Join the community", v:"__auth" }] : []),
              ].filter(({v})=> isAdmin || ns(v) !== "hidden").map(({icon, label, desc, v})=>{
                const state = ns(v);
                const chip = isAdmin && state==="hidden" ? ["HIDDEN","#9CA3AF"]
                           : state==="coming_soon"       ? ["SOON","#7C3AED"]
                           : state==="new"               ? ["NEW","#D97706"]
                           : null;
                return (
                <div key={v} className="mob-sheet-item" style={{opacity:state==="hidden"&&isAdmin?0.5:1}} onClick={()=>{ setShowMoreSheet(false); if(v==="__auth") openAuth(); else setView(v); }}>
                  <span className="mob-sheet-icon">{icon}</span>
                  <span style={{display:"flex",flexDirection:"column",gap:2}}>
                    <span className="mob-sheet-label" style={{display:"flex",alignItems:"center",gap:6}}>
                      {label}
                      {chip && <span style={{fontFamily:"'DM Mono',monospace",fontSize:7,letterSpacing:"0.1em",background:chip[1],color:"#fff",borderRadius:3,padding:"1px 5px",lineHeight:1.6}}>{chip[0]}</span>}
                    </span>
                    <span className="mob-sheet-desc">{desc}</span>
                  </span>
                </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function App() {
  return <LanguageProvider><AppInner/></LanguageProvider>;
}

// ─── Access Denied ────────────────────────────────────────────────────────────
